// Operations Management page. The calendar's shared texts live under
// profilePage.calendar (same components as the profile calendar).
const operations = {
  title: 'Operations Management',
  subtitle: 'Employee calendars, personnel reports and SC tracking in one place.',
  report: {
    button: 'Detailed Report',
    buttonHint: 'Download every employee’s visits and jobs in a date range as Excel',
    title: 'Detailed Report',
    subtitle: 'Choose the report’s date range',
    start: 'Start Date',
    end: 'End Date',
    help: 'The report holds every employee’s visits and jobs in this range: visits on the "Visitler" sheet, jobs on "İşler". The file is downloaded with Turkish headings.',
    generate: 'Create Report',
    generating: 'Preparing...',
    cancel: 'Cancel',
  },
  calendar: {
    title: 'Employee calendar',
    employee: 'Employee',
    employeePlaceholder: 'Choose an employee to see their calendar...',
    noEmployees: 'No employees found',
    pickTitle: 'Choose an employee to view',
    pickText: 'Once you pick someone in the "Employee" box above, their visits, jobs and leave appear here.',
    lazyNote: 'Data loads once an employee is selected.',
    sameAsProfile: 'This calendar shows the same records, the same way, as the employee’s own profile calendar: one bar across each visit window, the darker part is the visit day, a green tick marks the day it was done. Tap a record to open it; tap a day to see its list.',
    loading: 'Loading the calendar',
    loadingShort: 'Loading...',
    recordCounts: '{{visits}} visits · {{jobs}} jobs on record',
    refresh: 'Refresh',
    viewLabel: 'Calendar view',
    views: {
      month: 'Month',
      week: 'Week',
      day: 'Day',
      list: 'List',
    },
    prev: {
      month: 'Previous month',
      week: 'Previous week',
      day: 'Previous day',
      list: 'Previous month',
    },
    next: {
      month: 'Next month',
      week: 'Next week',
      day: 'Next day',
      list: 'Next month',
    },
    summaryLabel: 'Summary of the period shown',
    noEntries: 'Nothing planned',
    openDay: 'Open the day',
    openWindows_one: '{{count}} open window',
    openWindows_other: '{{count}} open windows',
    emptyMonth: 'Nothing this month',
    emptyMonthHint: 'Try the previous or next month.',
  },
  job: {
    title: 'Job Details',
    status: 'Status',
    employee: 'Employee',
    unknown: 'Not specified',
    start: 'Start',
    end: 'End',
    time: 'Time',
    description: 'Description',
    markDone: 'Mark as Done',
    markOpen: 'Mark as Not Done',
    delete: 'Delete',
    deleteTitle: 'Delete this job?',
    deleteMessage: 'The job "{{title}}" will be deleted permanently.',
    cascade: 'Delete This and Later Jobs',
    cancel: 'Cancel',
  },
  cascade: {
    titleVisit: 'Delete the Visit Series',
    titleJob: 'Delete the Job Series',
    warning: 'The records below will be deleted permanently. This can’t be undone.',
    employee: 'Employee',
    unknown: 'Not specified',
    patient: 'Patient ID',
    jobTitle: 'Job Title',
    from: 'Start date',
    fromValue: '{{date}} and later',
    scanning: 'Scanning records...',
    none: 'No records to delete.',
    count_one: 'Records to delete: {{count}}',
    count_other: 'Records to delete: {{count}}',
    selected: 'Selected',
    noProject: 'No project',
    visitNumber: 'Visit No: {{n}}',
    days_one: '{{count}} day',
    days_other: '{{count}} days',
    done: 'Completed',
    ongoing: 'In Progress',
    cancel: 'Cancel',
    confirm_one: 'Permanently Delete {{count}} Record',
    confirm_other: 'Permanently Delete {{count}} Records',
    deleting: 'Deleting...',
  },
  personnel: {
    title: 'Personnel Reports',
    description: 'Every active employee’s visits and jobs in the chosen period. Loads when opened.',
    activeEmployees_one: '{{count}} Active Employee',
    activeEmployees_other: '{{count}} Active Employees',
    loading: 'Loading data for all employees',
    period: 'Period',
    periods: {
      week: 'This Week',
      month: 'This Month',
      year: 'This Year',
      custom: 'Custom',
    },
    customStart: 'Period start',
    customEnd: 'Period end',
    excel: 'Download Excel',
    excelHint: 'Download the (filtered) employees in the table as Excel',
    summary: {
      totalVisits: 'Total Visits',
      completedVisits: 'Completed Visits',
      inProgressVisits: 'Visits in Progress',
      totalJobs: 'Total Jobs',
      completedJobs: 'Completed Jobs',
      uniqueProjects: 'Projects',
    },
    topTitle: 'Most Visits',
    visitsShort_one: '{{count}} visit',
    visitsShort_other: '{{count}} visits',
    rateShort: '{{rate}}% completed',
    tableHelp: 'Tap a heading to sort. Type 5, >5, <5, >=5 or <=5 in a number box to filter. Tap a row to open the employee’s visits and jobs.',
    columns: {
      employeeName: 'Employee',
      totalVisits: 'Visits',
      completedVisits: 'Completed V.',
      totalJobs: 'Jobs',
      completedJobs: 'Completed J.',
      projectsWorkedOn: 'Projects',
      completionRate: 'Success',
    },
    searchName: 'Search names...',
    example: 'e.g. {{value}}',
    filterLabel: '{{column}} filter',
    empty: 'No employee has records in this period',
    openEmployee: 'Open details for {{name}}',
    close: 'Close',
    successRate: 'Success',
    visitDetails_one: 'Visit Details ({{count}})',
    visitDetails_other: 'Visit Details ({{count}})',
    jobDetails_one: 'Job Details ({{count}})',
    jobDetails_other: 'Job Details ({{count}})',
    noVisits: 'No visits in this period',
    noJobs: 'No jobs in this period',
    noProject: 'No project',
    days_one: '{{count}} Day',
    days_other: '{{count}} Days',
    done: 'Completed',
    ongoing: 'In Progress',
  },
  sc: {
    title: 'SC Tracking Table',
    description: 'Project and site assignments of site coordinators, backup staff and nurses. Loads when opened.',
    badge: '{{sc}} SC · {{projects}} Projects',
    loading: 'Loading data',
    summary: {
      scPeople: 'SC Staff',
      projects: 'Projects',
      sites: 'Sites',
      activePatients: 'Active Patients',
    },
    search: 'Search SC, project, site, PI...',
    roleFilter: 'Role',
    roles: {
      all: 'All Roles',
      sc: 'Site Coordinator',
      backup: 'Backup Staff',
      nurse: 'Nurse',
      scSorumlusu: 'SC Lead',
    },
    statusFilter: 'Status',
    statuses: {
      all: 'All Statuses',
      active: 'Active',
      inactive: 'Inactive',
    },
    refresh: 'Refresh',
    refreshHint: 'Reload the data',
    clear: 'Clear',
    clearHint: 'Clear the filters',
    excel: 'Download Excel',
    active: 'Active',
    inactive: 'Inactive',
    groupCount_one: '{{count}} record',
    groupCount_other: '{{count}} records',
    columns: {
      scName: 'SC',
      employeeStatus: 'Status',
      phone: 'Phone',
      email: 'E-mail',
      serviceFte: 'Service-FTE',
      project: 'Project',
      protocolNumber: 'Protocol',
      indication: 'Indication',
      site: 'Site',
      pi: 'PI',
      delegation: 'Delegation',
      targetedPatients: 'Targeted Patients',
      screenedPatients: 'Screened',
      screenFailure: 'Screen Failure',
      randomization: 'Randomisation',
      currentPatients: 'Current Patients',
      sponsor: 'Sponsor',
    },
    empty: 'No records found',
    footer: {
      count_one: 'Showing {{count}} record',
      count_other: 'Showing {{count}} records',
      filteredFrom: '(filtered from {{total}})',
      grouped_one: 'grouped under {{count}} SC — sort by project or site to see a flat list',
      grouped_other: 'grouped under {{count}} SCs — sort by project or site to see a flat list',
    },
    toast: {
      loaded: 'SC tracking data loaded',
      loadError: 'Something went wrong while loading the SC tracking data',
      searchError: 'Something went wrong while searching',
      filterError: 'Something went wrong while filtering the table',
      exported: 'Excel file downloaded',
    },
  },
  toast: {
    load: 'Something went wrong while loading the data',
    loadAll: 'Something went wrong while loading all data',
    noData: 'Nothing to download',
    personnelExported: 'Personnel report downloaded!',
    exportError: 'Something went wrong while downloading the Excel file',
    jobDone: 'Job marked as done!',
    jobOpen: 'Job marked as in progress!',
    jobUpdateError: 'The job status couldn’t be updated',
    jobDeleted: 'Job deleted',
    jobDeleteError: 'The job couldn’t be deleted',
    cascadeNoEmployee: 'This record has no employee, so the series can’t be deleted',
    cascadeNoPatient: 'This visit has no Patient ID, so the series can’t be deleted',
    cascadeNoTitle: 'This job has no title, so the series can’t be deleted',
    cascadeNoDate: 'This record’s date couldn’t be read, so the series can’t be deleted',
    cascadeListError: 'The records to delete couldn’t be listed',
    mirrorError: 'Some mirror visits couldn’t be deleted; check the unblinded employee’s calendar.',
    cascadeDeleted_one: '{{count}} record deleted',
    cascadeDeleted_other: '{{count}} records deleted',
    cascadeDeleteError: 'Something went wrong while deleting the records',
    reportDates: 'Please choose the start and end dates',
    reportDone: 'Detailed report created! ({{visits}} visits, {{jobs}} jobs)',
    reportError: 'Something went wrong while creating the report',
  },
};

export default operations;
